import React, { useState, useEffect } from 'react';

import { IconX } from '@tabler/icons-react';

import { ComboDropdownValuePill, ComboDropdownValuePillProps } from './ComboDropdownValuePill';
import { usePopout, FloatingPortal } from 'ui-kit/hooks/usePopout';

export type DropdownPillListInputProps = Omit<ComboDropdownValuePillProps, 'value'> & {
  value: string[];
  onValueChanged: (value: string[]) => void;
  placeholder?: string;
  className?: string;
  valueToString?: (value: string[]) => string;
};

export function DropdownPillListInput({
  description,
  value,
  onValueChanged,
  placeholder,
  iconName,
  iconOnClick,
  valueElipses,
  valueToString,
  className,
}: DropdownPillListInputProps) {
  const { refs, getFloatingProps, getReferenceProps, isOpen, floatingStyles, setIsOpen } =
    usePopout();

  const [inputValue, setInputValue] = useState(value.join('\n'));

  useEffect(() => {
    setInputValue(value.join('\n'));
  }, [value]);

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const input = event.target.value;
    setInputValue(input);

    const values = input
      .split(/[\n,]+/)
      .map((val) => val.trim())
      .filter((val) => val.length > 0);

    onValueChanged(values);
  };

  const displayValue = valueToString ? valueToString(value) : `${value.length} items`;

  return (
    <>
      <ComboDropdownValuePill
        {...getReferenceProps()}
        ref={refs.setReference}
        description={description}
        className={className}
        isActive={isOpen}
        iconName={iconName}
        iconOnClick={iconOnClick}
        value={displayValue}
        valueElipses={valueElipses}
      />
      {isOpen ? (
        <FloatingPortal>
          {/* z-40: above Pill's remove-badge (z-30), below the app's
              modal/dialog tier (z-50). */}
          <div
            className="z-40"
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            {/* Matches PillButtonCombobox's flyout (border-gray-700/
                bg-gray-950 panel, same title+close row, same input
                treatment) — was border-less bg-gray-950 with no title/close
                and a textarea styled off a different, blue-accented token
                set (bg-background-canvas/text-input-text/ring-blue-500),
                the only flyout in the app that looked like it. */}
            <div className="w-64 rounded border border-gray-700 bg-gray-950 px-3 py-4 shadow-[0px_8px_16px_0px_rgba(13,13,13,0.9)]">
              <div className="flex items-center justify-between">
                <span className="text-xs text-white">{description}</span>
                <button
                  aria-label="Close"
                  className="bg-transparent border-none hover:bg-slate-700 active:bg-slate-700"
                  onClick={() => setIsOpen(false)}
                  tabIndex={0}
                >
                  <IconX color="white" size={20} />
                </button>
              </div>
              <textarea
                className="mt-1 h-32 w-full resize-none rounded border border-gray-700 bg-gray-950 p-2 text-white outline-none"
                placeholder={placeholder}
                value={inputValue}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </FloatingPortal>
      ) : null}
    </>
  );
}
